import { useCallback, useEffect, useState } from 'react'
import { usePersistedState } from './usePersistedState'

export const DRAFT_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000

export interface WorkspaceDraftState {
  text: string
  savedAt: number
}

const EMPTY_DRAFT: WorkspaceDraftState = {
  text: '',
  savedAt: 0,
}

export type WorkspaceDraftUpdater = string | ((prev: string) => string)

export interface UseWorkspaceDraftOptions {
  writeDelayMs?: number
}

/**
 * Shared workspace draft hook (WS5, S5).
 * Persists composer text per user and workspace in localStorage with a 300ms debounce.
 * Drafts older than 7 days are dropped on read.
 */
export function useWorkspaceDraft(
  key: string,
  options?: UseWorkspaceDraftOptions,
): readonly [string, (update: WorkspaceDraftUpdater) => void, () => void] {
  const activeKey = key || 'draft.__empty__'
  const writeDelayMs = options?.writeDelayMs ?? 300

  const [persisted, setPersisted] = usePersistedState<WorkspaceDraftState>(
    activeKey,
    EMPTY_DRAFT,
    {
      storage: 'local',
      scope: 'user-workspace',
      writeDelayMs,
      skipInitialWrite: true,
    },
  )

  const [mountTime] = useState(() => Date.now())
  const isExpired = Boolean(persisted && persisted.savedAt && mountTime - persisted.savedAt > DRAFT_MAX_AGE_MS)

  useEffect(() => {
    if (isExpired && persisted.text !== '') {
      setPersisted(EMPTY_DRAFT)
    }
  }, [isExpired, persisted.text, setPersisted])

  const text = isExpired ? '' : (persisted?.text ?? '')

  const clearDraft = useCallback(() => {
    setPersisted(EMPTY_DRAFT)
  }, [setPersisted])

  const setText = useCallback(
    (update: WorkspaceDraftUpdater) => {
      setPersisted((current) => {
        const prevText = current?.savedAt && Date.now() - current.savedAt > DRAFT_MAX_AGE_MS
          ? ''
          : (current?.text ?? '')
        const nextText = typeof update === 'function' ? update(prevText) : update
        if (!nextText) {
          return EMPTY_DRAFT
        }
        return {
          text: nextText,
          savedAt: Date.now(),
        }
      })
    },
    [setPersisted],
  )

  return [text, setText, clearDraft] as const
}
